import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { money } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Search, ChevronLeft, ChevronRight, Pencil, Package, Layers } from "lucide-react";
import { SortHeader, useSort } from "@/components/SortHeader";
import { toast } from "sonner";

const LIMIT = 40;
const VAT = 0.21;
const withVat = (net) => (parseFloat(net) || 0) * (1 + VAT);

export default function ProductList() {
  const [cat, setCat] = useState("mamul");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [edit, setEdit] = useState(null); // product being edited
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const params = { skip: page * LIMIT, limit: LIMIT };
    if (search.trim()) params.search = search.trim();
    if (cat !== "all") params.category = cat;
    const d = await api.stock(params);
    setItems(d.items);
    setTotal(d.total);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cat, page, search]);

  const openEdit = (p) => {
    setEdit(p);
    setForm({
      code: p.code || "", name: p.name || "", pack_info: p.pack_info || "",
      barcode: p.barcode || "", sale_price_net: p.sale_price_net ?? "", purchase_price: p.purchase_price ?? "",
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        code: form.code, name: form.name, pack_info: form.pack_info, barcode: form.barcode,
        sale_price_net: form.sale_price_net === "" ? 0 : parseFloat(form.sale_price_net),
        purchase_price: form.purchase_price === "" ? 0 : parseFloat(form.purchase_price),
      };
      const d = await api.updateStockProduct(edit.id, payload);
      setItems((prev) => prev.map((x) => (x.id === edit.id ? { ...x, ...d } : x)));
      toast.success("Ürün güncellendi");
      setEdit(null);
    } catch {
      toast.error("Güncelleme başarısız");
    } finally { setSaving(false); }
  };

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  const prodSort = useSort(items, {
    code: (p) => p.code || "",
    name: (p) => p.name || "",
    pack_info: (p) => p.pack_info || "",
    barcode: (p) => p.barcode || "",
    sale_price_net: (p) => parseFloat(p.sale_price_net) || 0,
    vat: (p) => withVat(p.sale_price_net),
    purchase_price: (p) => parseFloat(p.purchase_price) || 0,
    margin: (p) => {
      const net = parseFloat(p.sale_price_net) || 0;
      const buy = parseFloat(p.purchase_price) || 0;
      return net && buy ? net - buy : -Infinity;
    },
  });
  const sortedItems = prodSort.sorted;

  return (
    <div className="p-5 pt-20 lg:p-10 lg:pt-10">
      <PageHeader title="Ürün Listesi" subtitle="Ürün kodu, açıklama, ağırlık/adet, barkod ve fiyat bilgilerini düzenleyin (TVA %21)" />

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          {[["mamul", "Mamül", Package], ["hammadde", "Hammadde", Layers], ["all", "Tümü", null]].map(([k, l, Icon]) => (
            <button key={k} data-testid={`product-tab-${k}`} onClick={() => { setCat(k); setPage(0); }} className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${cat === k ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
              {Icon && <Icon className="h-3.5 w-3.5" />} {l}
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="product-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} placeholder="Kod / açıklama / barkod ara..." className="pl-9" />
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <table className="w-full min-w-[900px] text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <SortHeader label="Kod" sortId="code" sort={prodSort} className="px-4" />
              <SortHeader label="Açıklama" sortId="name" sort={prodSort} className="px-4" />
              <SortHeader label="Ağırlık/Adet" sortId="pack_info" sort={prodSort} className="px-4" />
              <SortHeader label="Barkod" sortId="barcode" sort={prodSort} className="px-4" />
              <SortHeader label="TVA'sız Satış" sortId="sale_price_net" sort={prodSort} align="right" className="px-4" />
              <SortHeader label="TVA'lı (%21)" sortId="vat" sort={prodSort} align="right" className="px-4" />
              <SortHeader label="Alış" sortId="purchase_price" sort={prodSort} align="right" className="px-4" />
              <SortHeader label="Kâr Marjı" sortId="margin" sort={prodSort} align="right" className="px-4" />
              <th className="px-4 py-3.5 text-right font-semibold"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={9} className="px-4 py-12 text-center text-muted-foreground">Ürün bulunamadı</td></tr>}
            {sortedItems.map((p) => {
              const net = parseFloat(p.sale_price_net) || 0;
              const buy = parseFloat(p.purchase_price) || 0;
              const margin = net && buy ? net - buy : null;
              const marginPct = net && buy ? ((net - buy) / buy) * 100 : null;
              return (
                <tr key={p.id} data-testid="product-row" className="border-b border-border/50 hover:bg-secondary/30">
                  <td className="px-4 py-3 font-mono text-teal-300">{p.code || "-"}</td>
                  <td className="px-4 py-3 font-medium">{p.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{p.pack_info || "-"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{p.barcode || "-"}</td>
                  <td className="px-4 py-3 text-right font-mono">{p.sale_price_net ? money(p.sale_price_net) : "-"}</td>
                  <td className="px-4 py-3 text-right font-mono text-emerald-400">{p.sale_price_net ? money(withVat(p.sale_price_net)) : "-"}</td>
                  <td className="px-4 py-3 text-right font-mono text-amber-300">{p.purchase_price ? money(p.purchase_price) : "-"}</td>
                  <td className="px-4 py-3 text-right font-mono">
                    {margin != null ? (
                      <span className={margin >= 0 ? "text-emerald-400" : "text-rose-400"} data-testid="product-margin">
                        {money(margin)} <span className="text-xs text-muted-foreground">(%{marginPct.toFixed(1)})</span>
                      </span>
                    ) : "-"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button data-testid="product-edit-btn" size="sm" variant="ghost" onClick={() => openEdit(p)} className="h-8 gap-1 text-xs"><Pencil className="h-3.5 w-3.5" /> Düzenle</Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <div className="text-xs text-muted-foreground">Toplam {total.toLocaleString("tr-TR")} · Sayfa {page + 1} / {totalPages}</div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}><ChevronLeft className="h-4 w-4" /> Önceki</Button>
          <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>Sonraki <ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle className="font-display">Ürün Düzenle</DialogTitle>
            <DialogDescription>Fiyat ve ürün bilgilerini güncelleyin. TVA'lı fiyat otomatik hesaplanır.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3.5 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Ürün Kodu</Label><Input data-testid="pf-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="mt-1.5 font-mono" /></div>
              <div><Label>Ağırlık / Adet</Label><Input data-testid="pf-pack" value={form.pack_info} onChange={(e) => setForm({ ...form, pack_info: e.target.value })} placeholder="215G*14" className="mt-1.5" /></div>
            </div>
            <div><Label>Açıklama</Label><Input data-testid="pf-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Barkod (CB / GTIN)</Label><Input data-testid="pf-barcode" value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} className="mt-1.5 font-mono" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>TVA'sız Satış Fiyatı</Label><Input data-testid="pf-net" type="number" value={form.sale_price_net} onChange={(e) => setForm({ ...form, sale_price_net: e.target.value })} className="mt-1.5" /></div>
              <div><Label>TVA'lı (%21)</Label><Input value={form.sale_price_net ? money(withVat(form.sale_price_net)) : ""} disabled className="mt-1.5 text-emerald-400" /></div>
            </div>
            <div><Label>Alış Tutarı</Label><Input data-testid="pf-purchase" type="number" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button data-testid="pf-save" onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90">{saving ? "Kaydediliyor..." : "Kaydet"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
